import type { AxiosError } from 'axios';

interface ClassValidatorError {
  target: Record<string, unknown>;
  value: unknown;
  property: string;
  children: unknown[];
  constraints: Record<string, string>;
}

interface ErrorResponse {
  message: string; // Always a string due to HTTP serialization
  error?: string;
  statusCode?: number;
}

/**
 * Process API errors and return user-friendly messages
 */
export const processApiError = (error: unknown): string => {
  if (!error) {
    return 'An unexpected error occurred. Please try again.';
  }

  // Handle Axios errors (API responses)
  if ((error as AxiosError)?.isAxiosError) {
    const axiosError = error as AxiosError;
    const response = axiosError.response;

    if (!response) {
      return 'Network error. Please check your connection and try again.';
    }

    const data = response.data as ErrorResponse;

    // Handle class-validator errors (400/422 with validation data)
    if ((response.status === 400 || response.status === 422) && data.message && typeof data.message === 'string') {
      // Try to parse as JSON first (class-validator errors are often stringified)
      try {
        const parsedMessage = JSON.parse(data.message);
        if (Array.isArray(parsedMessage)) {
          const firstItem = parsedMessage[0];
          if (firstItem && typeof firstItem === 'object' && 'property' in firstItem && 'constraints' in firstItem) {
            return processClassValidatorErrors(parsedMessage as ClassValidatorError[]);
          }
        }
      } catch {
        // If JSON parsing fails, treat as regular business logic error
      }

      return processBusinessLogicError(data.message, response.status);
    }

    // Handle business logic errors (400, 409, etc. with string message)
    if (typeof data.message === 'string') {
      return processBusinessLogicError(data.message, response.status);
    }

    // Handle other HTTP errors
    switch (response.status) {
      case 401:
        return 'You are not authorized to perform this action.';
      case 403:
        return 'You do not have permission to perform this action.';
      case 404:
        return 'The requested resource was not found.';
      case 409:
        return 'There is a conflict with the current state. Please refresh and try again.';
      case 500:
        return 'Server error. Please try again later.';
      default:
        return `Error ${response.status}: ${data.message || 'Something went wrong'}`;
    }
  }

  // Handle other error types
  if (error instanceof Error) {
    return error.message;
  }

  return 'An unexpected error occurred. Please try again.';
};

/**
 * Process class-validator errors into user-friendly messages
 */
const processClassValidatorErrors = (errors: ClassValidatorError[]): string => {
  if (!errors || errors.length === 0) {
    return 'Validation failed. Please check your input.';
  }

  // Extract all constraint messages
  const messages: string[] = [];

  errors.forEach((error) => {
    if (error.constraints) {
      Object.values(error.constraints).forEach((message) => {
        if (message && !messages.includes(message)) {
          messages.push(message);
        }
      });
    }
  });

  if (messages.length === 0) {
    return 'Validation failed. Please check your input.';
  }

  // Return the first message for single errors, or combine multiple
  if (messages.length === 1) {
    return messages[0];
  }

  return `Please fix the following issues:\n• ${messages.join('\n• ')}`;
};

/**
 * Process business logic errors - return the message as-is since DTOs should have user-friendly messages
 */
const processBusinessLogicError = (message: string, statusCode?: number): string => {
  // Handle specific HTTP status codes with generic messages
  switch (statusCode) {
    case 401:
      return 'You are not authorized to perform this action.';
    case 403:
      return 'You do not have permission to perform this action.';
    case 404:
      return 'The requested resource was not found.';
    case 500:
      return 'Server error. Please try again later.';
    default:
      // Return the original message as-is - DTOs should provide user-friendly messages
      return message;
  }
};
